import dayjs from 'dayjs';
import { create } from 'zustand';
import {
  ChatCounts,
  ClickData,
  Counts,
  DailyCount,
  DailyPaymentCount,
  DailyPointUsageCount,
  DailyNetSales,
  PaymentCounts,
  PaymentRankData,
  ProductCounts,
  ProductCountSummary,
  UserCounts,
  CumulativeHourlySales,
  SessionDurationData,
  ProductInteractionData,
  DailyPaymentNewCount,
  PaymentTimeSlotTrendsData,
  ProductClickLocationData,
  MonthlyPaymentComparisonData,
  PartnerProfileViewCountData,
  PartnerProfileClickCountData,
} from '../models/statistics';
import { ConvertDateTimeOptions, localTimeToUtcISOString } from '../utils/date';

interface StatisticsStore {
  chatCounts: ChatCounts | null;
  paymentCounts: PaymentCounts | null;
  productCounts: ProductCounts | null;
  dailyRefunds: DailyPaymentCount[] | null;
  dailyPayments: DailyPaymentNewCount[] | null;
  dailyPointUsage: DailyPointUsageCount[] | null;
  dailyNetSales: DailyNetSales[] | null;
  dailyPaymentError: DailyPaymentCount[] | null;
  dailyPaymentReady: DailyPaymentCount[] | null;
  dailyView: DailyCount[] | null;
  dailyProductView: ProductCountSummary[] | null;
  dailySession: DailyCount[] | null;
  dau: DailyCount[] | null;
  dailyJoin: DailyCount[] | null;
  countsUser?: UserCounts | null;
  countsDau: Counts | null;
  countsMau: Counts | null;
  countsVisit: Counts | null;
  countsNewParents: Counts | null;
  countsNewTutors: Counts | null;
  countsNewSellers: Counts | null;
  countsNewInstructors: Counts | null;
  cumulativeHourlySales: CumulativeHourlySales | null;
  sessionDuration: SessionDurationData[] | null;
  startDate: string | null; // UTC ISOString
  endDate: string | null; // UTC ISOString
  searchTextRank?: { _id: string; count: number }[];
  dateSearchRank?: {
    totalCount: number;
    dateSearchCount: number;
    rank: {
      _id: string;
      count: number;
      query?: any;
    }[];
  };
  noResultSearchTextRank?: { _id: string; count: number }[];
  paymentRankNaverTop?: PaymentRankData[];
  setPaymentRankNaverTop: (rank: PaymentRankData[]) => void;
  paymentRankNaverBottom?: PaymentRankData[];
  setPaymentRankNaverBottom: (rank: PaymentRankData[]) => void;
  paymentRankKakaoTop?: PaymentRankData[];
  setPaymentRankKakaoTop: (rank: PaymentRankData[]) => void;
  paymentRankKakaoBottom?: PaymentRankData[];
  setPaymentRankKakaoBottom: (rank: PaymentRankData[]) => void;
  paymentRankEzwelTop?: PaymentRankData[];
  setPaymentRankEzwelTop: (rank: PaymentRankData[]) => void;
  paymentRankEzwelBottom?: PaymentRankData[];
  setPaymentRankEzwelBottom: (rank: PaymentRankData[]) => void;
  paymentRankCommerceTop?: PaymentRankData[];
  setPaymentRankCommerceTop: (rank: PaymentRankData[]) => void;
  paymentRankCommerceBottom?: PaymentRankData[];
  setPaymentRankCommerceBottom: (rank: PaymentRankData[]) => void;
  paymentRankClassTop?: PaymentRankData[];
  setPaymentRankClassTop: (rank: PaymentRankData[]) => void;
  paymentRankClassBottom?: PaymentRankData[];
  setPaymentRankClassBottom: (rank: PaymentRankData[]) => void;
  refundRankSeller?: PaymentRankData[];
  setRefundRankSeller: (rank: PaymentRankData[]) => void;
  refundRankProduct?: PaymentRankData[];
  setRefundRankProduct: (rank: PaymentRankData[]) => void;
  userCountByState?: { state: string; count: number }[];
  setUserCountByState: (counts: { state: string; count: number }[]) => void;
  buyerRegionCounts?: { _id: string; count: number }[];
  setBuyerRegionCounts: (counts: { _id: string; count: number }[]) => void;
  buyerRegionPaymentAmounts?: { _id: string; totalAmount: number; count: number }[];
  setBuyerRegionPaymentAmounts: (amounts: { _id: string; totalAmount: number; count: number }[]) => void;
  productViewsByCategoryWeekly?: { [key: string]: any }[];
  setProductViewsByCategoryWeekly: (data: { [key: string]: any }[]) => void;
  productViewsByCategoryWeeklyRatios?: { [key: string]: any }[];
  setProductViewsByCategoryWeeklyRatios: (data: { [key: string]: any }[]) => void;
  dailyClassCounts?: { date: string; count: number; isPrediction?: boolean }[];
  setDailyClassCounts: (data: { date: string; count: number; isPrediction?: boolean }[]) => void;
  dailyFavoriteProductCounts?: {
    date: string;
    dailyCount: number;
    cumulativeCount: number;
    isPrediction?: boolean;
  }[];
  setDailyFavoriteProductCounts: (
    data: {
      date: string;
      dailyCount: number;
      cumulativeCount: number;
      isPrediction?: boolean;
    }[],
  ) => void;
  dailyFavoriteUserContentsCounts?: {
    date: string;
    dailyCount: number;
    cumulativeCount: number;
    isPrediction?: boolean;
  }[];
  setDailyFavoriteUserContentsCounts: (
    data: {
      date: string;
      dailyCount: number;
      cumulativeCount: number;
      isPrediction?: boolean;
    }[],
  ) => void;
  dailyFavoritePartnerCounts?: {
    date: string;
    dailyCount: number;
    cumulativeCount: number;
    isPrediction?: boolean;
  }[];
  setDailyFavoritePartnerCounts: (
    data: {
      date: string;
      dailyCount: number;
      cumulativeCount: number;
      isPrediction?: boolean;
    }[],
  ) => void;
  clicksRank?: ClickData[];
  setClicksRank: (rank: ClickData[]) => void;
  purchaseSourceCounts?:
    | {
        eventId: { actionId: string; keyword?: string; collectionId?: string };
        count: number;
        collectionTitle?: string;
        partnerName?: string;
      }[]
    | null;
  setPurchaseSourceCounts: (
    data?: {
      eventId: { actionId: string; keyword?: string; collectionId?: string };
      count: number;
      collectionTitle?: string;
      partnerName?: string;
    }[],
  ) => void;
  setChatCounts: (counts: ChatCounts) => void;
  setPaymentCounts: (counts: PaymentCounts) => void;
  setProductCounts: (counts: ProductCounts) => void;
  setDailyRefunds: (counts: DailyPaymentCount[]) => void;
  setDailyPayments: (counts: DailyPaymentNewCount[]) => void;
  setDailyPointUsage: (counts: DailyPointUsageCount[]) => void;
  setDailyNetSales: (counts: DailyNetSales[]) => void;
  setDailyPaymentError: (counts: DailyPaymentCount[]) => void;
  setDailyPaymentReady: (counts: DailyPaymentCount[]) => void;
  setDailyView: (counts: DailyCount[]) => void;
  setDailyProductView: (counts: ProductCountSummary[]) => void;
  setDailySession: (counts: DailyCount[]) => void;
  setDau: (counts: DailyCount[]) => void;
  setDailyJoin: (counts: DailyCount[]) => void;
  setUserCounts: (counts: UserCounts) => void;
  setDauCounts: (counts: Counts) => void;
  setMauCounts: (counts: Counts) => void;
  setVisitCounts: (counts: Counts) => void;
  setNewParentsCounts: (counts: Counts) => void;
  setNewTutorsCounts: (counts: Counts) => void;
  setNewSellersCounts: (counts: Counts) => void;
  setNewInstructorsCounts: (counts: Counts) => void;
  setSearchTextRank: (data?: { _id: string; count: number }[]) => void;
  setDateSearchRank: (data?: {
    totalCount: number;
    dateSearchCount: number;
    rank: {
      _id: string;
      count: number;
      query?: any;
    }[];
  }) => void;
  setNoResultSearchTextRank: (data?: { _id: string; count: number }[]) => void;
  setStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => void; // Local Dayjs Format
  setEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => void; // Local Dayjs Format
  // 상품 카테고리별 주간 조회수용 별도 날짜
  productViewsWeeklyStartDate: string | null;
  productViewsWeeklyEndDate: string | null;
  setProductViewsWeeklyStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  setProductViewsWeeklyEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  // 일별 찜한 상품 수용 별도 날짜
  dailyFavoriteProductCountsStartDate: string | null;
  dailyFavoriteProductCountsEndDate: string | null;
  setDailyFavoriteProductCountsStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  setDailyFavoriteProductCountsEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  dailyFavoriteUserContentsCountsStartDate: string | null;
  dailyFavoriteUserContentsCountsEndDate: string | null;
  setDailyFavoriteUserContentsCountsStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  setDailyFavoriteUserContentsCountsEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  dailyFavoritePartnerCountsStartDate: string | null;
  dailyFavoritePartnerCountsEndDate: string | null;
  setDailyFavoritePartnerCountsStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  setDailyFavoritePartnerCountsEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => void;
  setCumulativeHourlySales: (sales: CumulativeHourlySales | null) => void;
  setSessionDuration: (sessionDuration: SessionDurationData[]) => void;
  productInteraction: ProductInteractionData[] | null;
  setProductInteraction: (productInteraction: ProductInteractionData[]) => void;
  paymentTimeSlotTrends: PaymentTimeSlotTrendsData[] | null;
  setPaymentTimeSlotTrends: (paymentTimeSlotTrends: PaymentTimeSlotTrendsData[]) => void;
  productClickLocationData: ProductClickLocationData[] | null;
  setProductClickLocationData: (productClickLocationData: ProductClickLocationData[]) => void;
  partnerProfileViewCounts: PartnerProfileViewCountData[] | null;
  setPartnerProfileViewCounts: (partnerProfileViewCounts: PartnerProfileViewCountData[]) => void;
  partnerProfileClickCounts: PartnerProfileClickCountData[] | null;
  setPartnerProfileClickCounts: (partnerProfileClickCounts: PartnerProfileClickCountData[]) => void;
  monthlyPaymentComparison: MonthlyPaymentComparisonData[] | null;
  setMonthlyPaymentComparison: (monthlyPaymentComparison: MonthlyPaymentComparisonData[]) => void;
  dailyPurchaseSourceCounts: { date: string; actionId: string; count: number }[] | null;
  setDailyPurchaseSourceCounts: (data: { date: string; actionId: string; count: number }[]) => void;
}

export const useStatisticsStore = create<StatisticsStore>((set) => ({
  chatCounts: null,
  paymentCounts: null,
  productCounts: null,
  dailyRefunds: null,
  dailyPayments: null,
  dailyPointUsage: null,
  dailyNetSales: null,
  dailyPaymentError: null,
  dailyPaymentReady: null,
  dailyView: null,
  dailyProductView: null,
  dailySession: null,
  dau: null,
  dailyJoin: null,
  countsUser: null,
  countsDau: null,
  countsMau: null,
  countsVisit: null,
  countsNewParents: null,
  countsNewTutors: null,
  countsNewSellers: null,
  countsNewInstructors: null,
  cumulativeHourlySales: null,
  sessionDuration: null,
  productInteraction: null,
  paymentTimeSlotTrends: null,
  productClickLocationData: null,
  partnerProfileViewCounts: null,
  partnerProfileClickCounts: null,
  monthlyPaymentComparison: null,
  purchaseSourceCounts: null,
  dateSearchRank: undefined,
  startDate: localTimeToUtcISOString(dayjs().toISOString(), { timeToZero: true }),
  endDate: localTimeToUtcISOString(dayjs().add(1, 'day').toISOString(), { timeToZero: true }),
  productViewsWeeklyStartDate: localTimeToUtcISOString(dayjs().subtract(7, 'day').toISOString(), { timeToZero: true }),
  productViewsWeeklyEndDate: localTimeToUtcISOString(dayjs().toISOString(), { timeToZero: true }),
  dailyFavoriteProductCountsStartDate: localTimeToUtcISOString(dayjs().subtract(7, 'day').toISOString(), {
    timeToZero: true,
  }),
  dailyFavoriteProductCountsEndDate: localTimeToUtcISOString(dayjs().toISOString(), { timeToZero: true }),
  dailyFavoriteUserContentsCountsStartDate: localTimeToUtcISOString(dayjs().subtract(7, 'day').toISOString(), {
    timeToZero: true,
  }),
  dailyFavoriteUserContentsCountsEndDate: localTimeToUtcISOString(dayjs().toISOString(), { timeToZero: true }),
  dailyFavoritePartnerCountsStartDate: localTimeToUtcISOString(dayjs().subtract(7, 'day').toISOString(), {
    timeToZero: true,
  }),
  dailyFavoritePartnerCountsEndDate: localTimeToUtcISOString(dayjs().toISOString(), { timeToZero: true }),
  clicksRank: undefined,
  setClicksRank: (ranks: ClickData[]) => {
    set({ clicksRank: ranks });
  },
  setPurchaseSourceCounts: (
    data?: {
      eventId: { actionId: string; keyword?: string; collectionId?: string };
      count: number;
      collectionTitle?: string;
      partnerName?: string;
    }[],
  ) => {
    set({ purchaseSourceCounts: data });
  },
  setUserCountByState: (counts: { state: string; count: number }[]) => {
    set({ userCountByState: counts });
  },
  setBuyerRegionCounts: (counts: { _id: string; count: number }[]) => {
    set({ buyerRegionCounts: counts });
  },
  setBuyerRegionPaymentAmounts: (amounts: { _id: string; totalAmount: number; count: number }[]) => {
    set({ buyerRegionPaymentAmounts: amounts });
  },
  setProductViewsByCategoryWeekly: (data: { [key: string]: any }[]) => {
    set({ productViewsByCategoryWeekly: data });
  },
  setProductViewsByCategoryWeeklyRatios: (data: { [key: string]: any }[]) => {
    set({ productViewsByCategoryWeeklyRatios: data });
  },
  setDailyClassCounts: (data: { date: string; count: number; isPrediction?: boolean }[]) => {
    set({ dailyClassCounts: data });
  },
  setRefundRankSeller: (rank: PaymentRankData[]) => {
    set({ refundRankSeller: rank });
  },
  setRefundRankProduct: (rank: PaymentRankData[]) => {
    set({ refundRankProduct: rank });
  },
  setPaymentRankNaverTop: (rank: PaymentRankData[]) => {
    set({ paymentRankNaverTop: rank });
  },
  setPaymentRankNaverBottom: (rank: PaymentRankData[]) => {
    set({ paymentRankNaverBottom: rank });
  },
  setPaymentRankKakaoTop: (rank: PaymentRankData[]) => {
    set({ paymentRankKakaoTop: rank });
  },
  setPaymentRankKakaoBottom: (rank: PaymentRankData[]) => {
    set({ paymentRankKakaoBottom: rank });
  },
  setPaymentRankEzwelTop: (rank: PaymentRankData[]) => {
    set({ paymentRankEzwelTop: rank });
  },
  setPaymentRankEzwelBottom: (rank: PaymentRankData[]) => {
    set({ paymentRankEzwelBottom: rank });
  },
  setPaymentRankCommerceTop: (rank: PaymentRankData[]) => {
    set({ paymentRankCommerceTop: rank });
  },
  setPaymentRankCommerceBottom: (rank: PaymentRankData[]) => {
    set({ paymentRankCommerceBottom: rank });
  },
  setPaymentRankClassTop: (rank: PaymentRankData[]) => {
    set({ paymentRankClassTop: rank });
  },
  setPaymentRankClassBottom: (rank: PaymentRankData[]) => {
    set({ paymentRankClassBottom: rank });
  },
  setChatCounts: (chatCounts: ChatCounts) => {
    set({ chatCounts });
  },
  setPaymentCounts: (paymentCounts: PaymentCounts) => {
    set({ paymentCounts });
  },
  setProductCounts: (productCounts: ProductCounts) => {
    set({ productCounts });
  },
  setDailyRefunds: (dailyRefunds: DailyPaymentCount[]) => {
    set({ dailyRefunds: dailyRefunds });
  },
  setDailyPayments: (dailyPayments: DailyPaymentNewCount[]) => {
    set({ dailyPayments: dailyPayments });
  },
  setDailyPointUsage: (dailyPointUsage: DailyPointUsageCount[]) => {
    set({ dailyPointUsage: dailyPointUsage });
  },
  setDailyNetSales: (dailyNetSales: DailyNetSales[]) => {
    set({ dailyNetSales: dailyNetSales });
  },
  setDailyPaymentError: (dailyPayments: DailyPaymentCount[]) => {
    set({ dailyPaymentError: dailyPayments });
  },
  setDailyPaymentReady: (dailyPayments: DailyPaymentCount[]) => {
    set({ dailyPaymentReady: dailyPayments });
  },
  setDailyView: (dailyView: DailyCount[]) => {
    set({ dailyView: dailyView });
  },
  setDailyProductView: (dailyProductView: ProductCountSummary[]) => {
    set({ dailyProductView: dailyProductView });
  },
  setDailySession: (dailySession: DailyCount[]) => {
    set({ dailySession: dailySession });
  },
  setDau: (dau: DailyCount[]) => {
    set({ dau: dau });
  },
  setDailyJoin: (dailyJoin: DailyCount[]) => {
    set({ dailyJoin: dailyJoin });
  },
  setUserCounts: (counts: UserCounts) => {
    set({ countsUser: counts });
  },
  setDauCounts: (counts: Counts) => {
    set({ countsDau: counts });
  },
  setMauCounts: (counts: Counts) => {
    set({ countsMau: counts });
  },
  setVisitCounts: (counts: Counts) => {
    set({ countsVisit: counts });
  },
  setNewParentsCounts: (counts: Counts) => {
    set({ countsNewParents: counts });
  },
  setNewTutorsCounts: (counts: Counts) => {
    set({ countsNewTutors: counts });
  },
  setNewSellersCounts: (counts: Counts) => {
    set({ countsNewSellers: counts });
  },
  setNewInstructorsCounts: (counts: Counts) => {
    set({ countsNewInstructors: counts });
  },
  setCumulativeHourlySales: (sales: CumulativeHourlySales | null) => {
    set({ cumulativeHourlySales: sales });
  },
  setSessionDuration: (sessionDuration: SessionDurationData[]) => {
    set({ sessionDuration });
  },
  setStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ startDate: localTimeToUtcISOString(date, options) });
  },
  setEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ endDate: localTimeToUtcISOString(date, options) });
  },
  setProductViewsWeeklyStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ productViewsWeeklyStartDate: localTimeToUtcISOString(date, options) });
  },
  setProductViewsWeeklyEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ productViewsWeeklyEndDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoriteProductCountsStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ dailyFavoriteProductCountsStartDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoriteProductCountsEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ dailyFavoriteProductCountsEndDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoritePartnerCountsStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ dailyFavoritePartnerCountsStartDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoritePartnerCountsEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ dailyFavoritePartnerCountsEndDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoriteUserContentsCountsStartDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ dailyFavoriteUserContentsCountsStartDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoriteUserContentsCountsEndDate: (date: Date | null, options?: ConvertDateTimeOptions) => {
    set({ dailyFavoriteUserContentsCountsEndDate: localTimeToUtcISOString(date, options) });
  },
  setDailyFavoriteUserContentsCounts: (
    data: {
      date: string;
      dailyCount: number;
      cumulativeCount: number;
      isPrediction?: boolean;
    }[],
  ) => {
    set({ dailyFavoriteUserContentsCounts: data });
  },
  setDailyFavoritePartnerCounts: (
    data: {
      date: string;
      dailyCount: number;
      cumulativeCount: number;
      isPrediction?: boolean;
    }[],
  ) => {
    set({ dailyFavoritePartnerCounts: data });
  },
  setSearchTextRank: (data?: { _id: string; count: number }[]) => {
    set({ searchTextRank: data });
  },
  setNoResultSearchTextRank: (data?: { _id: string; count: number }[]) => {
    set({ noResultSearchTextRank: data });
  },
  setDateSearchRank: (data?: {
    totalCount: number;
    dateSearchCount: number;
    rank: {
      _id: string;
      count: number;
      query?: any;
    }[];
  }) => {
    set({ dateSearchRank: data });
  },
  setProductInteraction: (productInteraction: ProductInteractionData[]) => {
    set({ productInteraction });
  },
  setPaymentTimeSlotTrends: (paymentTimeSlotTrends: PaymentTimeSlotTrendsData[]) => {
    set({ paymentTimeSlotTrends });
  },
  setProductClickLocationData: (productClickLocationData: ProductClickLocationData[]) => {
    set({ productClickLocationData });
  },
  setPartnerProfileViewCounts: (partnerProfileViewCounts: PartnerProfileViewCountData[]) => {
    set({ partnerProfileViewCounts });
  },
  setPartnerProfileClickCounts: (partnerProfileClickCounts: PartnerProfileClickCountData[]) => {
    set({ partnerProfileClickCounts });
  },
  setMonthlyPaymentComparison: (monthlyPaymentComparison: MonthlyPaymentComparisonData[]) => {
    set({ monthlyPaymentComparison });
  },
  dailyPurchaseSourceCounts: null,
  setDailyPurchaseSourceCounts: (data: { date: string; actionId: string; count: number }[]) => {
    set({ dailyPurchaseSourceCounts: data });
  },
  setDailyFavoriteProductCounts: (
    data: {
      date: string;
      dailyCount: number;
      cumulativeCount: number;
      isPrediction?: boolean;
    }[],
  ) => {
    set({ dailyFavoriteProductCounts: data });
  },
}));
